import { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { renderDesignPNG } from "../lib/designRender";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Store } from "lucide-react";

export default function Checkout() {
  const { items, subtotal, shipping, tip, isPickup, discountableSubtotal } = useCart();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(null); // {code, amount}
  const [codeInput, setCodeInput] = useState("");
  const [note, setNote] = useState("");
  const [pickupInfo, setPickupInfo] = useState(null);
  const [exemptInput, setExemptInput] = useState("");
  const [exempt, setExempt] = useState(null); // {label}

  useEffect(() => {
    if (isPickup) {
      api.get("/storefront").then((r) => setPickupInfo(r.data.pickup)).catch(() => {});
    }
  }, [isPickup]);

  // Server-authoritative pre-payment summary via /api/cart/quote.
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    if (items.length === 0) { setQuote(null); return; }
    let cancelled = false;
    api.post("/cart/quote", {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.customization?.size || null })),
      pickup: isPickup,
      discount_code: discount?.code || null,
    }).then((r) => { if (!cancelled) setQuote(r.data); }).catch(() => { if (!cancelled) setQuote(null); });
    return () => { cancelled = true; };
  }, [items, isPickup, discount?.code]);

  // Live sales-tax estimate via /api/cart/tax-preview (debounced on address change).
  const [taxPreview, setTaxPreview] = useState(null); // { available, tax_amount, exempt }
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
  });

  useEffect(() => {
    if (items.length === 0 || isPickup) { setTaxPreview(null); return; }
    if (!form.state.trim() || !form.postal_code.trim()) { setTaxPreview(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.post("/cart/tax-preview", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.customization?.size || null })),
        discount_code: discount?.code || null,
        tax_exempt_code: exemptInput.trim() || null,
        email: form.email || null,
        address: { line1: form.address_line1, city: form.city, state: form.state, postal_code: form.postal_code, country: form.country },
      }).then((r) => { if (!cancelled) setTaxPreview(r.data); }).catch(() => { if (!cancelled) setTaxPreview(null); });
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [items, isPickup, discount?.code, exempt, exemptInput, form.state, form.postal_code, form.country, form.city, form.address_line1, form.email]);

  const discountAmt = quote?.discount ?? (discount?.amount || 0);
  const serverSubtotal = quote?.subtotal ?? subtotal;
  const serverShipping = quote ? quote.shipping : shipping;
  const estTax = taxPreview?.available && !taxPreview?.exempt ? (taxPreview.tax_amount || 0) : 0;
  const baseTotal = quote ? quote.total : Math.max(0, subtotal - (discount?.amount || 0)) + shipping;
  const total = baseTotal + (tip || 0) + estTax;

  const applyDiscount = async () => {
    const code = codeInput.trim();
    if (!code) return;
    try {
      const r = await api.post("/discounts/validate", { code, subtotal: discountableSubtotal });
      setDiscount({ code: r.data.code, amount: r.data.amount });
      toast.success(`Code ${r.data.code} applied`);
    } catch (e) {
      setDiscount(null);
      toast.error(e.response?.data?.detail || "Invalid code");
    }
  };

  const applyExempt = async () => {
    const code = exemptInput.trim();
    if (!code) return;
    try {
      const r = await api.post("/tax-exempt/validate", { code });
      setExempt({ label: r.data.label });
      toast.success("Tax exemption applied — no sales tax");
    } catch (e) {
      setExempt(null);
      toast.error(e.response?.data?.detail || "Invalid exemption code");
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif-display text-4xl text-[var(--navy)]">Your cart is empty.</h1>
        <button onClick={() => nav("/shop")} className="btn-primary mt-6">Browse Shop</button>
      </div>
    );
  }

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payloadItems = await Promise.all(
        items.map(async (i) => {
          let customization = i.customization;
          if (i.customization?.layers?.length) {
            const render = await renderDesignPNG(i.customization.layers).catch(() => null);
            customization = { ...i.customization, design_render: render || null };
          }
          return { product_id: i.product_id, quantity: i.quantity, customization };
        })
      );
      const payload = {
        items: payloadItems,
        shipping: form,
        origin_url: window.location.origin,
        discount_code: discount?.code || null,
        tax_exempt_code: exemptInput.trim() || null,
        customer_note: note.trim() || null,
        tip: tip || 0,
        pickup: isPickup || false,
      };
      const res = await api.post("/checkout/session", payload);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Couldn't start checkout. Try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-12" data-testid="checkout-page">
      <div className="font-mono-ui text-[11px] tracking-[0.3em] text-[var(--crimson)]">SECURE CHECKOUT</div>
      <h1 className="font-serif-display text-5xl text-[var(--navy)] mt-2">{isPickup ? "Almost there — a few details." : "Where should we ship it?"}</h1>

      {isPickup && (
        <div className="mt-6 card-rustic p-5 border-l-4 border-[var(--crimson)] flex items-start gap-3" data-testid="checkout-pickup-info">
          <Store className="w-5 h-5 text-[var(--crimson)] mt-0.5 flex-shrink-0"/>
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--crimson)]">Local Pickup — No Shipping</div>
            <div className="font-serif-display text-xl text-[var(--navy)] mt-1">Pick up at the shop</div>
            {pickupInfo?.address && (
              <div className="text-sm text-[var(--ink-soft)] mt-1" data-testid="checkout-pickup-address">{pickupInfo.address}</div>
            )}
            {pickupInfo?.hours ? (
              <div className="text-sm text-[var(--ink-soft)] mt-0.5" data-testid="checkout-pickup-hours"><span className="font-mono-ui text-[10px] tracking-[0.15em] uppercase text-[var(--navy)]">Hours:</span> {pickupInfo.hours}</div>
            ) : (
              <div className="text-sm text-[var(--ink-soft)] mt-0.5">We'll email you the moment your order is ready to grab.</div>
            )}
            <div className="text-xs text-[var(--ink-soft)]/80 mt-2">We still need your contact info below so we can reach you about your order.</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-10 mt-10">
        <div className="lg:col-span-2 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name" required value={form.full_name} onChange={onChange("full_name")} testId="shipping-full-name"/>
            <Field label="Email" required type="email" value={form.email} onChange={onChange("email")} testId="shipping-email"/>
            <Field label="Phone (optional)" value={form.phone} onChange={onChange("phone")} testId="shipping-phone"/>
            <Field label="Country" required value={form.country} onChange={onChange("country")} testId="shipping-country"/>
          </div>
          <Field label="Street address" required value={form.address_line1} onChange={onChange("address_line1")} testId="shipping-address1"/>
          <Field label="Apt / Suite (optional)" value={form.address_line2} onChange={onChange("address_line2")} testId="shipping-address2"/>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="City" required value={form.city} onChange={onChange("city")} testId="shipping-city"/>
            <Field label="State" required value={form.state} onChange={onChange("state")} testId="shipping-state"/>
            <Field label="Zip / Postal" required value={form.postal_code} onChange={onChange("postal_code")} testId="shipping-postal"/>
          </div>
          <label className="block">
            <span className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--navy)] mb-1.5 block">Messages, comments, or special requests (optional)</span>
            <textarea
              data-testid="checkout-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="Tell Papa G anything special about your order — name spellings, colors, gift notes, deadlines…"
              className="input-field resize-y"
            />
          </label>
        </div>

        <div className="card-rustic p-6 h-fit lg:sticky lg:top-28">
          <div className="font-mono-ui text-[11px] tracking-[0.25em] uppercase text-[var(--navy)] mb-4">Your Order</div>
          <div className="space-y-2 max-h-60 overflow-auto">
            {items.map((it) => (
              <div key={it.line_id} className="flex justify-between text-sm">
                <span className="text-[var(--ink-soft)] truncate pr-3">{it.product_name}{it.customization?.size ? ` · ${it.customization.size}` : ""} <span className="text-[var(--ink-soft)]/70">× {it.quantity}</span></span>
                <span className="font-mono-ui">${(it.unit_price * it.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[rgba(27,42,65,0.1)] mt-4 pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between text-[var(--ink-soft)]"><span>Subtotal</span><span className="font-mono-ui">${serverSubtotal.toFixed(2)}</span></div>
            {discount && (
              <div className="flex justify-between text-[var(--crimson)]"><span>Discount ({discount.code})</span><span className="font-mono-ui">−${discountAmt.toFixed(2)}</span></div>
            )}
            {discount && items.some((i) => i.exclude_from_discounts) && (
              <div className="text-[11px] text-[var(--ink-soft)] leading-snug" data-testid="checkout-discount-exclusion-note">
                Some items aren't eligible for this code — the discount applies to eligible items only.
              </div>
            )}
            <div className="flex justify-between text-[var(--ink-soft)]"><span>Shipping{isPickup ? " (Pickup)" : ""}</span><span className="font-mono-ui" data-testid="checkout-shipping">{isPickup ? "PICKUP · $0.00" : serverShipping === 0 ? "FREE" : `$${serverShipping.toFixed(2)}`}</span></div>
            {!isPickup && items.some((i) => i.exclude_from_free_shipping) && (
              <div className="text-[11px] text-[var(--ink-soft)] leading-snug" data-testid="checkout-shipping-exclusion-note">
                Some items always ship for their own fee and don't qualify for free shipping.
              </div>
            )}
            {tip > 0 && (
              <div className="flex justify-between text-[var(--ink-soft)]"><span>Tip 💛</span><span className="font-mono-ui" data-testid="checkout-tip">${Number(tip).toFixed(2)}</span></div>
            )}
            {!isPickup && taxPreview?.exempt && (
              <div className="flex justify-between text-[var(--ink-soft)]"><span>Est. sales tax</span><span className="font-mono-ui" data-testid="checkout-tax-estimate">Exempt</span></div>
            )}
            {!isPickup && taxPreview?.available && !taxPreview?.exempt && (
              <div className="flex justify-between text-[var(--ink-soft)]"><span>Est. sales tax</span><span className="font-mono-ui" data-testid="checkout-tax-estimate">${estTax.toFixed(2)}</span></div>
            )}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              placeholder="Discount code"
              className="input-field flex-1 text-sm"
              data-testid="checkout-discount-input"
            />
            <button type="button" onClick={applyDiscount} className="btn-ghost px-3 text-xs" data-testid="checkout-discount-apply">Apply</button>
          </div>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={exemptInput}
              onChange={(e) => setExemptInput(e.target.value.toUpperCase())}
              placeholder="Tax-exempt code (optional)"
              className="input-field flex-1 text-sm"
              data-testid="checkout-exempt-input"
            />
            <button type="button" onClick={applyExempt} className="btn-ghost px-3 text-xs" data-testid="checkout-exempt-apply">Apply</button>
          </div>
          <div className="border-t border-[rgba(27,42,65,0.1)] mt-3 pt-3 flex justify-between items-baseline">
            <span className="font-serif-display text-xl text-[var(--navy)]">Total</span>
            <span className="font-serif-display text-3xl text-[var(--crimson)]" data-testid="checkout-total">${total.toFixed(2)}</span>
          </div>
          <div className="text-[11px] text-[var(--ink-soft)] mt-1 text-right" data-testid="checkout-tax-note">
            {exempt ? `Tax exempt${exempt.label ? ` — ${exempt.label}` : ""}` : (taxPreview?.available && !taxPreview?.exempt ? "Estimated tax — finalized at payment" : "Sales tax calculated at payment")}
          </div>
          <button type="submit" disabled={loading} className="btn-primary btn-crimson w-full justify-center mt-6" data-testid="checkout-submit-button">
            <Lock className="w-4 h-4"/> {loading ? "Redirecting…" : "Pay with Stripe"}
          </button>
          <div className="font-mono-ui text-[10px] tracking-[0.2em] text-[var(--ink-soft)] text-center mt-3">SSL ENCRYPTED · STRIPE SECURED</div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testId }) {
  return (
    <label className="block">
      <span className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--navy)] mb-1.5 block">{label}{required && <span className="text-[var(--crimson)] ml-1">*</span>}</span>
      <input data-testid={testId} type={type} required={required} value={value} onChange={onChange} className="input-field"/>
    </label>
  );
}
